/**
 * MULTI-FACTOR AUTHENTICATION, AT NO COST.
 *
 *   "the MFA business: nobody is set up for it, not even me. how can we set it
 *    up in such a way that it costs nothing? and then enrol the two staff
 *    members who are currently working at Orchid?"
 *
 * TOTP -- the six-digit code from an authenticator app -- is part of Supabase
 * Auth on every plan, free tier included. Supabase generates the secret, stores
 * it in auth.mfa_factors, and verifies the code. There is no third party in the
 * loop, nothing is sent anywhere, and nothing is billed per use.
 *
 * SMS factors are the ones that cost money, because they need an SMS provider
 * and every message is charged. Orchid enrols none, and script 98 refuses to
 * count one: only a VERIFIED TOTP factor makes an account enrolled, so the
 * "costs nothing" property is enforced in the database rather than remembered
 * in a policy document.
 *
 * WHAT THE DATABASE IS TOLD, AND WHAT IT WORKS OUT FOR ITSELF
 *
 * Nothing here tells the database that somebody is enrolled. It cannot: script
 * 98 refuses a hand-written mfa_enrolled from any signed-in session. All this
 * does is ask the database to LOOK -- fn_refresh_my_mfa_state() reads
 * auth.mfa_factors and brings the cached column into line with it.
 *
 * That distinction is the whole feature. admin_users.mfa_enrolled existed for
 * months, gated every sensitive permission, and was written by nothing at all;
 * a screen that set it directly would have been the same bug with a nicer
 * surface.
 *
 * LOSING THE AUTHENTICATOR
 *
 * Supabase requires an already-verified session to un-enrol a factor, so
 * somebody who loses their phone cannot clear it themselves and no button here
 * can do it for them. The factor is removed from the Supabase dashboard
 * (Authentication -> Users -> the person -> remove factor) by whoever owns the
 * project. Said on the screen, not just here.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const fail = (error) => ({ data: null, error });

const NO_BACKEND = 'This portal cannot reach the database.';

/**
 * What this session has actually proved.
 *
 *   currentLevel 'aal1'  — a password, and nothing else
 *   nextLevel    'aal2'  — there is a factor on this account still to clear
 *
 * `mustVerify` is the one question every caller has: is somebody sitting at a
 * half-finished sign-in? It is deliberately FALSE when the levels cannot be
 * read at all. A portal that demanded a code because a lookup failed would be
 * asserting something it does not know, and the person it locks out is the one
 * with the worst connection.
 */
export async function assuranceLevel() {
  if (!isSupabaseConfigured()) return fail(NO_BACKEND);
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) return fail(error.message || 'This session could not be checked.');
  const current = data?.currentLevel || null;
  const next = data?.nextLevel || null;
  return {
    data: { current, next, mustVerify: Boolean(current && next && current !== next) },
    error: null,
  };
}

/** The TOTP factors on this login, verified or not. */
export async function myFactors() {
  if (!isSupabaseConfigured()) return fail(NO_BACKEND);
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) return fail(error.message || 'Your authenticator could not be read.');
  const totp = (data?.totp || []).filter((f) => f.factor_type === 'totp' || f.factorType === 'totp');
  const all = totp.length ? totp : (data?.all || []).filter((f) => f.factor_type === 'totp');
  return {
    data: {
      verified: all.filter((f) => f.status === 'verified'),
      unverified: all.filter((f) => f.status !== 'verified'),
    },
    error: null,
  };
}

/**
 * Start enrolment: Supabase makes a secret and hands back a QR code to scan.
 *
 * ABANDONED ATTEMPTS ARE CLEARED FIRST. Somebody who opens this panel, walks
 * away, and comes back tomorrow leaves an unverified factor behind, and a
 * second enroll() against the same friendly name is refused — which would read
 * as "MFA is broken" when the truth is "you already started this once".
 * Unverified factors prove nothing and protect nothing, so removing them costs
 * nothing either.
 */
export async function beginEnrolment(friendlyName) {
  if (!isSupabaseConfigured()) return fail(NO_BACKEND);

  const existing = await myFactors();
  if (!existing.error) {
    for (const f of existing.data.unverified) {
      // Best effort. If this fails the enroll below will say so in Supabase's
      // own words, which is more useful than a message invented here.
      // eslint-disable-next-line no-await-in-loop
      await supabase.auth.mfa.unenroll({ factorId: f.id });
    }
  }

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: friendlyName || `Orchid Portal ${new Date().toISOString().slice(0, 10)}`,
  });
  if (error) return fail(error.message || 'Enrolment could not be started.');

  return {
    data: {
      factorId: data?.id,
      // An SVG data URI Supabase renders for us. The secret is shown alongside
      // it because a phone cannot scan the screen it is standing next to, and
      // typing thirty-two characters beats not enrolling.
      qr: data?.totp?.qr_code || null,
      secret: data?.totp?.secret || null,
    },
    error: null,
  };
}

/**
 * Finish enrolment by proving the authenticator works, then ask the database to
 * look at what Supabase now holds.
 *
 * THE ORDER IS THE POINT. Verify first; only then refresh. The portal never
 * tells the database that somebody is enrolled — it tells it to go and check.
 */
export async function confirmEnrolment(factorId, code) {
  if (!isSupabaseConfigured()) return fail(NO_BACKEND);
  if (!factorId) return fail('Start the setup again — that enrolment has expired.');

  const clean = (code || '').replace(/\s+/g, '');
  if (!/^\d{6}$/.test(clean)) {
    return fail('That is a six-digit code from your authenticator app.');
  }

  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: clean });
  if (error) {
    // Supabase's own words, because "invalid code" and "the code expired, the
    // clock on that phone is out" need different things done about them.
    return fail(error.message || 'That code was not accepted.');
  }

  return refreshMyMfaState();
}

/**
 * The code, at sign-in, for somebody already enrolled.
 *
 * Picks the verified factor rather than taking one as an argument: there is one
 * authenticator per staff account by design, and a screen that asks "which of
 * your factors?" is a screen built for a problem Orchid does not have.
 */
export async function clearTheSecondFactor(code) {
  if (!isSupabaseConfigured()) return fail(NO_BACKEND);

  const clean = (code || '').replace(/\s+/g, '');
  if (!/^\d{6}$/.test(clean)) {
    return fail('That is a six-digit code from your authenticator app.');
  }

  const factors = await myFactors();
  if (factors.error) return fail(factors.error);

  const factor = factors.data.verified[0];
  if (!factor) {
    return fail('There is no authenticator on this account. Sign out and back in.');
  }

  const { error } = await supabase.auth.mfa.challengeAndVerify({
    factorId: factor.id,
    code: clean,
  });
  if (error) return fail(error.message || 'That code was not accepted.');
  return { data: true, error: null };
}

/**
 * Ask the database to read the authenticator and bring its own record into
 * line. Called after enrolment, and on every portal load — so somebody whose
 * factor was removed stops being marked enrolled the next time they arrive
 * rather than carrying a stale yes.
 */
export async function refreshMyMfaState() {
  if (!isSupabaseConfigured()) return fail(NO_BACKEND);
  const { data, error } = await supabase.rpc('fn_refresh_my_mfa_state');
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('This portal needs a database update that has not been applied yet.');
    }
    return fail(error.message || 'Your enrolment could not be confirmed.');
  }
  return { data: (Array.isArray(data) ? data[0] : data) || null, error: null };
}

/**
 * WHAT THIS PERSON'S OWN SECOND FACTOR LOOKS LIKE RIGHT NOW.
 *
 *   "when the mfa is removed, the staff member must get the orange
 *    authenticator setup box on their screen UNTIL they have set it up again,
 *    then it can disappear."
 *
 * Read from the CREDENTIAL rather than the cached column, which matters at both
 * ends of the moment this exists for: somebody reset five minutes ago must see
 * the box before their cached row catches up, and somebody who has just
 * enrolled in another tab must stop seeing it.
 *
 * It also carries WHO removed the last one and WHEN. An orange box appearing
 * without explanation reads as a fault; the same box saying "Sipho removed your
 * authenticator on Friday" reads as a thing that happened for a reason.
 */
export async function myMfaStanding() {
  if (!isSupabaseConfigured()) return fail(NO_BACKEND);
  const { data, error } = await supabase.rpc('fn_my_mfa_standing');
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('This portal needs a database update that has not been applied yet.');
    }
    return fail(error.message || 'Your authenticator could not be checked.');
  }
  // No row means "not active Orchid staff", which is not an error and must not
  // be reported as one — the portal simply shows them no enrolment prompt.
  return { data: (Array.isArray(data) ? data[0] : data) || null, error: null };
}

/**
 * Remove somebody else's authenticator, because they have lost the phone it
 * was on.
 *
 * THIS GRANTS NOTHING, and the wording on the screen has to carry that. Every
 * permission canon marks requires_mfa reads the credential, so deleting the
 * factor CLOSES those permissions for that person until they enrol again. The
 * button is help, not access.
 *
 * The reason is not optional and is not a formality: 'reset_staff_mfa' is
 * marked requires_reason_capture, and script 118 refuses anything too short to
 * be a sentence. "Lost their phone" and "they left and I want their access"
 * look identical in a log that records neither.
 */
export async function resetStaffMfa(adminUserId, reason) {
  if (!isSupabaseConfigured()) return fail(NO_BACKEND);
  if (!adminUserId) return fail('Which staff member?');

  const clean = String(reason || '').trim();
  if (clean.length < 8) {
    return fail('Say why the authenticator is being removed — a sentence, not a word.');
  }

  const { data, error } = await supabase.rpc('fn_reset_staff_mfa', {
    p_admin_user_id: adminUserId,
    p_reason: clean,
  });
  if (error) {
    if (error.code === 'PGRST202') {
      return fail('This portal needs a database update that has not been applied yet.');
    }
    // The database's own words. "you cannot reset your own authenticator" and
    // "you do not have permission" need different things done about them, and
    // a message invented here would flatten both into "that did not work".
    return fail(error.message || 'That authenticator could not be removed.');
  }
  return { data: (Array.isArray(data) ? data[0] : data) || null, error: null };
}
